import { useState, useRef, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Send,
  Loader2,
  Bot,
  User,
  Settings,
  ChevronLeft,
  ChevronDown,
  Sparkles,
  Shield,
  AlertCircle,
  Square,
  Info,
  Globe,
  AudioLines,
  LayoutGrid
} from 'lucide-react'
import GraffitiLogo from '../../components/GraffitiLogo'

// Other SynthetIQ Signals systems this demo can preview. None of these have
// a live backend this public page can safely call, so every one of them is
// a simulated, clearly-labeled preview - only ALEN (the Scrambler's actual
// assistant) uses the live /api/chat proxy. Readiness labels follow the
// founder-assessed AVAILABLE/PILOT/BUILDING taxonomy used elsewhere in this
// company's materials, and deliberately avoid any claim not confirmed live
// (no TRL numbers, no compliance claims, no orchestration claims beyond
// what's actually built).
const READINESS_STYLES = {
  AVAILABLE: { label: 'Available', dot: 'bg-emerald-500', chip: 'bg-emerald-900/30 text-emerald-400 border-emerald-700/30', box: 'bg-emerald-900/20 border-emerald-700/30', text: 'text-emerald-200', subtext: 'text-emerald-300/80', icon: 'text-emerald-400' },
  PILOT: { label: 'Pilot', dot: 'bg-cyan-500', chip: 'bg-cyan-900/30 text-cyan-400 border-cyan-700/30', box: 'bg-cyan-900/20 border-cyan-700/30', text: 'text-cyan-200', subtext: 'text-cyan-300/80', icon: 'text-cyan-400' },
  BUILDING: { label: 'Building', dot: 'bg-amber-500', chip: 'bg-amber-900/30 text-amber-400 border-amber-700/30', box: 'bg-amber-900/20 border-amber-700/30', text: 'text-amber-200', subtext: 'text-amber-300/80', icon: 'text-amber-400' },
}

function getEpisodIQResponse(userMessage) {
  const msg = userMessage.toLowerCase()
  if (msg.includes('what') && (msg.includes('do') || msg.includes('is'))) {
    return "EpisodIQ reads medical bills and invoices - pulling out CPT/HCPCS/ICD billing codes, line items, and totals - and turns them into structured, queryable data you can ask questions about directly."
  }
  if (msg.includes('duplicate') || msg.includes('error') || msg.includes('mistake')) {
    return "One of EpisodIQ's core jobs is catching duplicate charges and billing inconsistencies across a batch of documents, so a double-billed line item doesn't slip through unnoticed."
  }
  if (msg.includes('privacy') || msg.includes('secure') || msg.includes('data')) {
    return "Documents are processed the same privacy-first way as the rest of our stack - your billing data stays governed, not handed off to a generic cloud OCR service."
  }
  if (msg.includes('ready') || msg.includes('live') || msg.includes('available') || msg.includes('status')) {
    return "The core OCR, billing-code analysis, and document Q&A are built and verified internally. This chat is a simulated preview for the public demo, not a live connection to the real system."
  }
  const defaults = [
    "That's the kind of question EpisodIQ is built to help with - ask me about parsing a bill, catching billing errors, or how it handles medical billing codes.",
    "In the real product, EpisodIQ would pull that detail straight out of the document. This preview is simulated, but the underlying OCR and billing-analysis engine is real and already built.",
  ]
  return defaults[Math.floor(Math.random() * defaults.length)]
}

function getForensIQResponse(userMessage) {
  const msg = userMessage.toLowerCase()
  if (msg.includes('what') && (msg.includes('do') || msg.includes('is'))) {
    return "ForensIQ manages evidence, incidents, individuals, and locations for a case - with hashed/encrypted evidence storage, verification, and a full audit log of who touched what, when."
  }
  if (msg.includes('evidence') || msg.includes('track') || msg.includes('audit') || msg.includes('log')) {
    return "Every piece of evidence is hashed and encrypted on intake, and every access or change is written to an audit log - so chain-of-custody questions have a real answer, not a guess."
  }
  if (msg.includes('map') || msg.includes('location') || msg.includes('geospatial')) {
    return "A geospatial layer - mapping evidence and incidents to locations on a timeline - is currently being built on top of the core case-management system."
  }
  if (msg.includes('ready') || msg.includes('live') || msg.includes('available') || msg.includes('status')) {
    return "The core case/evidence-management workflow is built and verified internally; the newer geospatial mapping feature is still under active development. This chat is a simulated preview, not a live connection."
  }
  const defaults = [
    "Ask me about how ForensIQ tracks evidence, manages a case, or keeps an audit trail of every action taken.",
    "That's a real ForensIQ capability area. This preview is simulated, but the case-management and evidence-verification engine behind it is built and already in use.",
  ]
  return defaults[Math.floor(Math.random() * defaults.length)]
}

function getWalkBackwardResponse(userMessage) {
  const msg = userMessage.toLowerCase()
  if (msg.includes('what') && (msg.includes('do') || msg.includes('is'))) {
    return "WalkBackward is a genealogy research platform - building out a family tree from documents and records, with human review built into the process rather than guessing from an algorithm alone."
  }
  if (msg.includes('tree') || msg.includes('family') || msg.includes('record') || msg.includes('ancestor')) {
    return "The idea is to intake records (certificates, photos, documents), extract what's useful from them, and let a person confirm each connection before it becomes part of the family tree."
  }
  if (msg.includes('ready') || msg.includes('live') || msg.includes('available') || msg.includes('status')) {
    return "WalkBackward is under active development - the family-tree tooling and document intake are being built out now. This chat is a simulated preview of where it's headed, not a live system yet."
  }
  const defaults = [
    "Ask me what WalkBackward does with a document, how it builds out a family tree, or what's still being worked on.",
    "That's part of the vision for WalkBackward. It's still being built, so this preview is simulated rather than connected to a live family tree.",
  ]
  return defaults[Math.floor(Math.random() * defaults.length)]
}

function getConductorResponse(userMessage) {
  const msg = userMessage.toLowerCase()
  if (msg.includes('what') && (msg.includes('do') || msg.includes('is'))) {
    return "Conductor is the workflow-orchestration engine behind the 23Controls™ framework - it deploys and runs defined workflows rather than requiring everything to be wired by hand."
  }
  if (msg.includes('workflow') || msg.includes('automate') || msg.includes('orchestrat')) {
    return "Today, Conductor reliably runs single-step workflows end to end. Multi-step orchestration - chaining several steps together - is still being built out."
  }
  if (msg.includes('ready') || msg.includes('live') || msg.includes('available') || msg.includes('status')) {
    return "Single-step workflow deployment works today; full multi-step orchestration is still in progress. This chat is a simulated preview, not a live connection to a running workflow."
  }
  const defaults = [
    "Ask me what kind of workflow Conductor runs, or how far along multi-step orchestration is.",
    "That's the direction Conductor is headed. This preview is simulated, but single-step workflow deployment is already real and working.",
  ]
  return defaults[Math.floor(Math.random() * defaults.length)]
}

function getAttributionResponse(userMessage) {
  const msg = userMessage.toLowerCase()
  if (msg.includes('what') && (msg.includes('do') || msg.includes('is'))) {
    return "Attribution is a multi-touch marketing-attribution engine - it credits every touchpoint in a customer's journey, not just the first or last one, using a deterministic Fibonacci-decay model."
  }
  if (msg.includes('fibonacci') || msg.includes('decay') || msg.includes('model') || msg.includes('how')) {
    return "Fibonacci decay weights each touchpoint so that ones closer to the conversion count more, but earlier touchpoints still get real, non-zero credit - rather than an all-or-nothing first/last-touch model."
  }
  if (msg.includes('ready') || msg.includes('live') || msg.includes('available') || msg.includes('status')) {
    return "The attribution math engine itself is live and tested. The workflow-orchestration layer and any customer-facing dashboard around it are not built yet - this chat preview is simulated."
  }
  const defaults = [
    "Ask me how Attribution credits a touchpoint, what Fibonacci decay means here, or what's still being built around it.",
    "That's a real question for the Attribution engine, which is live and tested. This chat preview itself is simulated, and the orchestration/dashboard layer around the engine isn't built yet.",
  ]
  return defaults[Math.floor(Math.random() * defaults.length)]
}

const PRODUCT_PERSONAS = [
  {
    id: 'episodiq',
    label: 'EpisodIQ',
    product: 'EpisodIQ',
    tagline: 'Turns medical bills and invoices into structured, queryable data.',
    readiness: 'PILOT',
    readinessNote: "Core OCR, billing-code analysis, and document Q&A are built and verified internally. This chat is a simulated preview for the public demo.",
    welcome: "Hi! This is a preview of EpisodIQ, which turns medical bills and invoices into structured, searchable data. Ask me what it can do, how it handles billing codes, or how it keeps your documents private.",
    respond: getEpisodIQResponse,
  },
  {
    id: 'forensiq',
    label: 'ForensIQ',
    product: 'ForensIQ',
    tagline: 'Evidence, incident, and case management with a verifiable audit trail.',
    readiness: 'PILOT',
    readinessNote: "Core case and evidence-management workflows are built and verified internally; a newer geospatial mapping feature is still under active development. This chat is a simulated preview for the public demo.",
    welcome: "Hi! This is a preview of ForensIQ, a case and evidence-management system with a verifiable audit trail. Ask me what it can do, how it tracks evidence, or how access is controlled.",
    respond: getForensIQResponse,
  },
  {
    id: 'walkbackward',
    label: 'WalkBackward',
    product: 'WalkBackward',
    tagline: 'Genealogy research - building your family tree from records and documents.',
    readiness: 'BUILDING',
    readinessNote: "WalkBackward is under active development. This chat is a simulated preview of where it's headed, not a live system.",
    welcome: "Hi! This is a preview of WalkBackward, a genealogy research platform. Ask me what it can do, how it builds a family tree, or what's still being built.",
    respond: getWalkBackwardResponse,
  },
  {
    id: 'conductor',
    label: 'Conductor',
    product: 'Conductor',
    tagline: 'Workflow orchestration for the 23Controls™ framework.',
    readiness: 'BUILDING',
    readinessNote: "Single-step workflow deployment runs today; multi-step orchestration is still being built. This chat is a simulated preview.",
    welcome: "Hi! This is a preview of Conductor, the workflow-orchestration engine behind the 23Controls™ framework. Ask me what it automates, or how far along multi-step orchestration is.",
    respond: getConductorResponse,
  },
  {
    id: 'attribution',
    label: 'Attribution',
    product: 'Attribution',
    tagline: 'Multi-touch marketing attribution, modeled with Fibonacci decay.',
    readiness: 'AVAILABLE',
    readinessNote: "The attribution math engine itself is live and tested. Its workflow-orchestration layer and any customer-facing dashboard are not built yet, and this chat preview is simulated.",
    welcome: "Hi! This is a preview of Attribution, our multi-touch marketing-attribution engine. Ask me how it credits touchpoints, or what Fibonacci decay means here.",
    respond: getAttributionResponse,
  },
]

const ALEN_PERSONA = { id: 'alen', label: 'ALEN', product: 'Graffiti™ Scrambler', tagline: "The Scrambler's built-in assistant - live by default." }

const ALL_PERSONAS = [ALEN_PERSONA, ...PRODUCT_PERSONAS]

// Supported languages for voice input/output and the live demo's replies.
const SUPPORTED_LANGUAGES = [
  { code: 'en-US', name: 'English (US)', nativeName: 'English', flag: '🇺🇸' },
  { code: 'en-GB', name: 'English (UK)', nativeName: 'English', flag: '🇬🇧' },
  { code: 'en-AU', name: 'English (Australia)', nativeName: 'English', flag: '🇦🇺' },
  { code: 'es-ES', name: 'Spanish (Spain)', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'es-MX', name: 'Spanish (Mexico)', nativeName: 'Español', flag: '🇲🇽' },
  { code: 'fr-FR', name: 'French (France)', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'fr-CA', name: 'French (Canada)', nativeName: 'Français', flag: '🇨🇦' },
  { code: 'de-DE', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'it-IT', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)', nativeName: 'Português', flag: '🇧🇷' },
  { code: 'pt-PT', name: 'Portuguese (Portugal)', nativeName: 'Português', flag: '🇵🇹' },
  { code: 'nl-NL', name: 'Dutch', nativeName: 'Nederlands', flag: '🇳🇱' },
  { code: 'ru-RU', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
  { code: 'ja-JP', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' },
  { code: 'ko-KR', name: 'Korean', nativeName: '한국어', flag: '🇰🇷' },
  { code: 'zh-CN', name: 'Chinese (Simplified)', nativeName: '中文', flag: '🇨🇳' },
  { code: 'zh-TW', name: 'Chinese (Traditional)', nativeName: '中文', flag: '🇹🇼' },
  { code: 'hi-IN', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳' },
  { code: 'ar-SA', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦' },
  { code: 'tr-TR', name: 'Turkish', nativeName: 'Türkçe', flag: '🇹🇷' },
  { code: 'vi-VN', name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'th-TH', name: 'Thai', nativeName: 'ไทย', flag: '🇹🇭' },
  { code: 'id-ID', name: 'Indonesian', nativeName: 'Bahasa Indonesia', flag: '🇮🇩' },
  { code: 'sv-SE', name: 'Swedish', nativeName: 'Svenska', flag: '🇸🇪' },
  { code: 'pl-PL', name: 'Polish', nativeName: 'Polski', flag: '🇵🇱' },
]

// Map browser/system locales to one of the codes above.
const LOCALE_MAPPING = {
  'en': 'en-US', 'en-us': 'en-US', 'en-gb': 'en-GB', 'en-au': 'en-AU',
  'es': 'es-ES', 'es-mx': 'es-MX', 'fr': 'fr-FR', 'fr-ca': 'fr-CA',
  'de': 'de-DE', 'it': 'it-IT', 'pt': 'pt-BR', 'pt-br': 'pt-BR', 'pt-pt': 'pt-PT',
  'nl': 'nl-NL', 'ru': 'ru-RU', 'ja': 'ja-JP', 'ko': 'ko-KR',
  'zh': 'zh-CN', 'zh-cn': 'zh-CN', 'zh-tw': 'zh-TW',
  'hi': 'hi-IN', 'ar': 'ar-SA', 'tr': 'tr-TR', 'vi': 'vi-VN',
  'th': 'th-TH', 'id': 'id-ID', 'sv': 'sv-SE', 'pl': 'pl-PL',
}

function detectUserLanguage() {
  if (typeof navigator === 'undefined') return 'en-US'
  const browserLang = (navigator.language || navigator.userLanguage || 'en-US').toLowerCase()
  if (LOCALE_MAPPING[browserLang]) return LOCALE_MAPPING[browserLang]
  const baseLang = browserLang.split('-')[0]
  return LOCALE_MAPPING[baseLang] || 'en-US'
}

const WELCOME_MESSAGES = {
  'en-US': "Hello! I'm your Graffiti™ Scrambler assistant. I can help you with questions, creative tasks, or just have a conversation. You can type or use voice input. How can I assist you today?",
  'en-GB': "Hello! I'm your Graffiti™ Scrambler assistant. How may I assist you today?",
  'en-AU': "G'day! I'm your Graffiti™ Scrambler assistant. How can I help you today?",
  'es-ES': "¡Hola! Soy tu asistente Graffiti™ Scrambler. ¿Cómo puedo ayudarte hoy?",
  'es-MX': "¡Hola! Soy tu asistente Graffiti™ Scrambler. ¿Cómo puedo ayudarte hoy?",
  'fr-FR': "Bonjour ! Je suis votre assistant Graffiti™ Scrambler. Comment puis-je vous aider ?",
  'fr-CA': "Bonjour ! Je suis votre assistant Graffiti™ Scrambler. Comment puis-je vous aider ?",
  'de-DE': "Hallo! Ich bin Ihr Graffiti™ Scrambler-Assistent. Wie kann ich Ihnen helfen?",
  'it-IT': "Ciao! Sono il tuo assistente Graffiti™ Scrambler. Come posso aiutarti?",
  'pt-BR': "Olá! Sou seu assistente Graffiti™ Scrambler. Como posso ajudá-lo?",
  'pt-PT': "Olá! Sou o seu assistente Graffiti™ Scrambler. Como posso ajudá-lo?",
  'ja-JP': "こんにちは！Graffiti™ Scramblerアシスタントです。どのようにお手伝いできますか？",
  'ko-KR': "안녕하세요! Graffiti™ Scrambler 어시스턴트입니다. 어떻게 도와드릴까요?",
  'zh-CN': "你好！我是 Graffiti™ Scrambler 助手。今天我能帮你什么？",
  'ru-RU': "Привет! Я ваш ассистент Graffiti™ Scrambler. Как я могу помочь?",
  'ar-SA': "مرحبا! أنا مساعد Graffiti™ Scrambler. كيف يمكنني مساعدتك؟",
  'hi-IN': "नमस्ते! मैं आपका Graffiti™ Scrambler सहायक हूं। मैं आपकी कैसे मदद कर सकता हूं?",
}

// Audio visualization component
function AudioVisualizer({ isActive }) {
  const bars = 12

  return (
    <div className="flex items-center justify-center gap-1 h-16">
      {[...Array(bars)].map((_, i) => (
        <motion.div
          key={i}
          className="w-1.5 bg-gradient-to-t from-purple-600 to-purple-400 rounded-full"
          animate={{
            height: isActive
              ? `${20 + Math.random() * 40}px`
              : '8px',
          }}
          transition={{
            duration: 0.15,
            repeat: isActive ? Infinity : 0,
            repeatType: 'reverse',
          }}
        />
      ))}
    </div>
  )
}

// Custom dropdown used for the language/voice pickers
function Dropdown({ value, onChange, options, icon: Icon, label, renderOption }) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const selectedOption = options.find(opt => opt.value === value) || options[0]

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-xs text-void-500 mb-1.5">{label}</label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-3 py-2.5 bg-void-800/80 border border-void-700/50 rounded-xl text-left text-void-200 hover:bg-void-700/80 transition-colors"
      >
        {Icon && <Icon className="w-4 h-4 text-purple-400 shrink-0" />}
        <span className="flex-1 truncate text-sm">
          {renderOption ? renderOption(selectedOption) : selectedOption?.label}
        </span>
        <ChevronDown className={`w-4 h-4 text-void-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 mt-2 w-full max-h-60 overflow-auto bg-void-900 border border-void-700/50 rounded-xl shadow-xl shadow-void-950/50"
          >
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => { onChange(option.value); setIsOpen(false) }}
                className={`w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-void-800/80 transition-colors ${
                  option.value === value ? 'bg-purple-900/30 text-purple-300' : 'text-void-300'
                }`}
              >
                {renderOption ? renderOption(option) : option.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// Message bubble component
function MessageBubble({ message, isUser, isLoading }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}
    >
      <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
        isUser
          ? 'bg-synth-600'
          : 'bg-gradient-to-br from-purple-600 to-purple-700'
      }`}>
        {isUser ? (
          <User className="w-5 h-5 text-white" />
        ) : (
          <Bot className="w-5 h-5 text-white" />
        )}
      </div>

      <div className={`max-w-[80%] ${isUser ? 'text-right' : ''}`}>
        <div className={`inline-block px-4 py-3 rounded-2xl ${
          isUser
            ? 'bg-synth-600 text-white rounded-tr-sm'
            : 'bg-void-800/80 text-void-100 rounded-tl-sm'
        }`}>
          {isLoading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Thinking...</span>
            </div>
          ) : (
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
          )}
        </div>
        {message.timestamp && (
          <p className={`text-xs text-void-500 mt-1 ${isUser ? 'text-right' : ''}`}>
            {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        )}
      </div>
    </motion.div>
  )
}

// Device frame component
function ScramblerDevice({ children, isListening, isSpeaking }) {
  return (
    <div className="relative">
      <div className={`absolute inset-0 rounded-[3rem] blur-2xl transition-opacity duration-500 ${
        isListening ? 'bg-purple-500/30 opacity-100' : isSpeaking ? 'bg-cyan-500/20 opacity-100' : 'opacity-0'
      }`} />

      <div className="relative w-72 bg-gradient-to-b from-purple-600 via-purple-700 to-purple-800 rounded-[2.5rem] p-6 shadow-2xl shadow-purple-900/50">
        <div className="bg-void-950 rounded-2xl overflow-hidden border-4 border-purple-500/30">
          {children}
        </div>

        <div className="mt-4 px-4">
          <div className="flex flex-col gap-1.5">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-1 bg-purple-900/50 rounded-full" />
            ))}
          </div>
        </div>

        <div className="flex justify-center mt-4">
          <GraffitiLogo className="h-8 w-8" animated={false} />
        </div>

        <div className="flex justify-center mt-3">
          <motion.div
            animate={{
              opacity: isListening ? [0.5, 1, 0.5] : isSpeaking ? [0.7, 1, 0.7] : 0.3,
              scale: isListening || isSpeaking ? [1, 1.2, 1] : 1,
            }}
            transition={{ duration: isListening ? 0.5 : 1, repeat: Infinity }}
            className={`w-3 h-3 rounded-full ${
              isListening ? 'bg-red-500 shadow-lg shadow-red-500/50' :
              isSpeaking ? 'bg-cyan-400 shadow-lg shadow-cyan-400/50' :
              'bg-purple-400'
            }`}
          />
        </div>
      </div>
    </div>
  )
}

// Intelligent demo responses based on user input
function getSmartDemoResponse(userMessage) {
  const msg = userMessage.toLowerCase()

  if (msg.match(/^(hi|hello|hey|greetings|good morning|good afternoon|good evening)/)) {
    return "Hello! I'm your Graffiti™ Scrambler assistant. I'm here to help you with questions, creative tasks, or just have a friendly conversation. What would you like to explore today?"
  }

  if (msg.includes('scrambler') || msg.includes('graffiti') || msg.includes('what are you') || msg.includes('who are you')) {
    return "I'm the AI assistant built into the Graffiti™ Scrambler - a privacy-focused home AI device created by Mimeograph Holdings. Unlike cloud-based assistants, I run entirely on your device. Your data never leaves your home, protected by RSA 2048 encryption. I can help with questions, creative tasks, home automation, and much more!"
  }

  if (msg.includes('privacy') || msg.includes('data') || msg.includes('secure') || msg.includes('safe')) {
    return "Privacy is at the core of everything we do. The Scrambler processes everything locally on your device - no cloud uploads, no data harvesting. Your conversations, your data, your rules. We use RSA 2048 encryption and are fully compliant with SOC II, HIPAA, and PCI standards. That's our promise: We Come in Peace."
  }

  if (msg.includes('price') || msg.includes('cost') || msg.includes('how much') || msg.includes('afford')) {
    return "The Graffiti™ Scrambler is designed to be accessible to everyone, regardless of socioeconomic status. That's our founding philosophy. We offer flexible pricing tiers and the device starts at an affordable price point. Check out our products page for current pricing and plans!"
  }

  if (msg.includes('how') && (msg.includes('work') || msg.includes('function'))) {
    return "The Scrambler connects to our secure AI model marketplace through an encrypted network. You choose which models to use - from our flagship Graffiti™ LLM to specialized agents for different tasks. Everything runs locally using our unique networking approach, so your personal, health, and financial data never leaves your home."
  }

  if (msg.includes('feature') || msg.includes('can you do') || msg.includes('capabilities') || msg.includes('what can')) {
    return "I can help with a wide range of tasks! Ask me questions, brainstorm ideas, help with creative writing, analyze documents, control smart home devices, set reminders, and much more. The Case Marketplace also offers specialized extensions for things like health tracking, financial planning, and educational content."
  }

  if (msg.includes('write') || msg.includes('story') || msg.includes('poem') || msg.includes('creative')) {
    return "I'd love to help with creative tasks! I can write stories, poems, scripts, or help you brainstorm ideas. Just tell me what you're working on - the genre, mood, characters, or any details you have in mind. In the full Scrambler, I can even speak my creations aloud!"
  }

  if (msg.includes('api') || msg.includes('developer') || msg.includes('integrate') || msg.includes('sdk')) {
    return "For developers, we offer comprehensive APIs and SDKs in Python, JavaScript, Go, and Rust. You can integrate Scrambler capabilities into your own applications while maintaining our privacy-first approach. Check out our Developer Tools section for documentation and examples!"
  }

  if (msg.includes('thank') || msg.includes('thanks')) {
    return "You're welcome! I'm always here to help. Is there anything else you'd like to know about the Scrambler or anything I can assist you with?"
  }

  if (msg.includes('bye') || msg.includes('goodbye') || msg.includes('see you')) {
    return "Goodbye! Remember, I'm always here whenever you need me. The Scrambler never sleeps - just say the word and I'll be ready to help. We Come in Peace! 💜"
  }

  const defaultResponses = [
    "That's an interesting question! In the full Scrambler experience, I'd connect to our AI models to give you a comprehensive answer. For now in demo mode, I can tell you that I'm designed to help with exactly these kinds of queries - thoughtfully and privately.",
    "Great topic! The Scrambler is built to handle conversations just like this. With the full device, I'd tap into our secure model network to provide detailed, personalized responses - all while keeping your data completely private.",
    "I appreciate you exploring the demo! While I'm showing simulated responses right now, the real Scrambler uses advanced AI models to have natural, helpful conversations about any topic you're curious about.",
    "That's exactly the kind of thing I'm designed to help with! The Graffiti™ Scrambler combines powerful AI with uncompromising privacy. Your questions and my answers stay on your device - no cloud, no data collection.",
    "I'm here to help! In demo mode, I can show you how the interface works and tell you about the Scrambler's capabilities. With the full device, I'd provide detailed, AI-powered responses to help you with whatever you need."
  ]

  return defaultResponses[Math.floor(Math.random() * defaultResponses.length)]
}

export default function ScramblerDemoPage() {
  const [selectedLanguage, setSelectedLanguage] = useState(detectUserLanguage())
  const [selectedPersonaId, setSelectedPersonaId] = useState('alen')
  const [messages, setMessages] = useState([
    {
      id: 1,
      role: 'assistant',
      content: WELCOME_MESSAGES[detectUserLanguage()] || WELCOME_MESSAGES['en-US'],
      timestamp: Date.now(),
    }
  ])
  const [inputText, setInputText] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isListening, setIsListening] = useState(false)
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [error, setError] = useState(null)
  const [showSettings, setShowSettings] = useState(false)
  const [liveMode, setLiveMode] = useState(true)
  const [remaining, setRemaining] = useState(null)

  const [availableVoices, setAvailableVoices] = useState([])
  const [selectedVoice, setSelectedVoice] = useState('')
  const [voicesLoaded, setVoicesLoaded] = useState(false)

  const messagesEndRef = useRef(null)
  const recognitionRef = useRef(null)
  const synthRef = useRef(null)
  const sessionIdRef = useRef(
    (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `demo-${Date.now()}-${Math.random().toString(36).slice(2)}`
  )

  const currentLanguage = SUPPORTED_LANGUAGES.find(l => l.code === selectedLanguage) || SUPPORTED_LANGUAGES[0]
  const currentPersona = ALL_PERSONAS.find(p => p.id === selectedPersonaId) || ALEN_PERSONA
  const isAlen = currentPersona.id === 'alen'

  // Voices available for the currently selected language (TTS voice list
  // depends entirely on the visitor's own OS/browser).
  const filteredVoices = availableVoices.filter(voice =>
    voice.lang.toLowerCase().startsWith(selectedLanguage.split('-')[0].toLowerCase())
  )
  const voiceOptions = filteredVoices.length > 0
    ? filteredVoices.map(voice => ({ value: voice.name, label: voice.name }))
    : [{ value: '', label: voicesLoaded ? 'No voice for this language' : 'Loading voices...' }]
  const languageOptions = SUPPORTED_LANGUAGES.map(lang => ({
    value: lang.code, label: lang.name, flag: lang.flag,
  }))

  // Reset the conversation when the visitor switches language or persona.
  // ALEN's welcome is localized; the product-preview personas are English-
  // only simulated previews, so they use their own fixed welcome text.
  useEffect(() => {
    const welcomeContent = isAlen
      ? (WELCOME_MESSAGES[selectedLanguage] || WELCOME_MESSAGES['en-US'])
      : currentPersona.welcome
    setMessages([{
      id: Date.now(),
      role: 'assistant',
      content: welcomeContent,
      timestamp: Date.now(),
    }])
    setError(null)
  }, [selectedLanguage, selectedPersonaId])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      synthRef.current = window.speechSynthesis
    }
  }, [])

  // Load available TTS voices and pick one that actually matches the
  // selected language, instead of matching on vendor name alone (which
  // previously picked whichever "Google"/"Natural" voice came first,
  // regardless of its accent/language).
  useEffect(() => {
    if (typeof window === 'undefined' || !synthRef.current) return

    const loadVoices = () => {
      const voices = synthRef.current.getVoices()
      if (voices.length > 0) {
        setAvailableVoices(voices)
        setVoicesLoaded(true)
      }
    }

    loadVoices()
    if (synthRef.current.onvoiceschanged !== undefined) {
      synthRef.current.onvoiceschanged = loadVoices
    }
  }, [])

  // Re-pick the preferred voice whenever the language changes or voices load.
  useEffect(() => {
    if (availableVoices.length === 0) return
    const langCode = selectedLanguage.split('-')[0].toLowerCase()
    const langVoices = availableVoices.filter(v => v.lang.toLowerCase().startsWith(langCode))
    if (langVoices.length > 0) {
      const preferred = langVoices.find(v =>
        v.name.includes('Natural') || v.name.includes('Enhanced') || v.name.includes('Premium') || v.name.includes('Google')
      ) || langVoices[0]
      setSelectedVoice(preferred.name)
    } else {
      // No voice installed for this language - leave unset so speech
      // synthesis falls back to the browser default rather than an
      // unrelated language's voice.
      setSelectedVoice('')
    }
  }, [selectedLanguage, availableVoices])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      if (SpeechRecognition) {
        recognitionRef.current = new SpeechRecognition()
        recognitionRef.current.continuous = false
        recognitionRef.current.interimResults = true
        recognitionRef.current.lang = selectedLanguage

        recognitionRef.current.onresult = (event) => {
          const transcript = Array.from(event.results)
            .map(result => result[0].transcript)
            .join('')
          setInputText(transcript)

          if (event.results[0].isFinal) {
            setIsListening(false)
          }
        }

        recognitionRef.current.onerror = (event) => {
          console.error('Speech recognition error:', event.error)
          setIsListening(false)
          if (event.error === 'not-allowed') {
            setError('Microphone access denied. Please enable microphone permissions.')
          }
        }

        recognitionRef.current.onend = () => {
          setIsListening(false)
        }
      }
    }
  }, [])

  // Keep speech recognition's language in sync with the picker.
  useEffect(() => {
    if (recognitionRef.current) {
      recognitionRef.current.lang = selectedLanguage
    }
  }, [selectedLanguage])

  const toggleListening = useCallback(() => {
    if (!recognitionRef.current) {
      setError('Speech recognition is not supported in your browser.')
      return
    }

    if (isListening) {
      recognitionRef.current.stop()
      setIsListening(false)
    } else {
      setError(null)
      recognitionRef.current.lang = selectedLanguage
      recognitionRef.current.start()
      setIsListening(true)
    }
  }, [isListening, selectedLanguage])

  const speak = useCallback((text) => {
    if (!synthRef.current || isMuted) return

    synthRef.current.cancel()

    const utterance = new SpeechSynthesisUtterance(text)
    utterance.rate = 1.0
    utterance.pitch = 1.0
    utterance.volume = 1.0
    utterance.lang = selectedLanguage

    const voice = selectedVoice
      ? availableVoices.find(v => v.name === selectedVoice)
      : null

    if (voice) {
      utterance.voice = voice
    }

    utterance.onstart = () => setIsSpeaking(true)
    utterance.onend = () => setIsSpeaking(false)
    utterance.onerror = () => setIsSpeaking(false)

    synthRef.current.speak(utterance)
  }, [isMuted, selectedVoice, selectedLanguage, availableVoices])

  const stopSpeaking = useCallback(() => {
    if (synthRef.current) {
      synthRef.current.cancel()
      setIsSpeaking(false)
    }
  }, [])

  const sendMessage = async (content) => {
    if (!content.trim()) return

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: content.trim(),
      timestamp: Date.now(),
    }

    setMessages(prev => [...prev, userMessage])
    setInputText('')
    setIsLoading(true)
    setError(null)

    try {
      let responseContent

      if (!isAlen) {
        // Every non-ALEN persona is a simulated preview only - none of
        // these systems has a backend this public page can safely call.
        await new Promise(resolve => setTimeout(resolve, 800 + Math.random() * 1200))
        responseContent = currentPersona.respond(content)
      } else if (liveMode && remaining !== 0) {
        try {
          const response = await fetch('/api/chat', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              message: content.trim().slice(0, 500),
              session_id: sessionIdRef.current,
              language: selectedLanguage,
            }),
          })

          if (!response.ok) {
            // 429 (rate/session limit), 502/503 (unavailable/unconfigured) all
            // fall through to the canned preview below rather than erroring
            // out in front of a visitor.
            throw new Error(`demo endpoint: ${response.status}`)
          }

          const data = await response.json()
          responseContent = data.reply
          setRemaining(data.remaining)
        } catch {
          setLiveMode(false)
          await new Promise(resolve => setTimeout(resolve, 500))
          responseContent = getSmartDemoResponse(content)
        }
      } else {
        await new Promise(resolve => setTimeout(resolve, 800 + Math.random() * 1200))
        responseContent = getSmartDemoResponse(content)
      }

      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: responseContent,
        timestamp: Date.now(),
      }

      setMessages(prev => [...prev, assistantMessage])

      if (!isMuted) {
        speak(responseContent)
      }

    } catch (err) {
      console.error('Error:', err)
      setError(err.message || 'Failed to get response. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    sendMessage(inputText)
  }

  return (
    <div className="pt-24 pb-16 min-h-screen">
      <div className="fixed inset-0 bg-gradient-to-br from-purple-950/30 via-void-950 to-synth-950/20 -z-10" />
      <div className="fixed inset-0 grid-overlay opacity-20 -z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/products/home" className="inline-flex items-center gap-2 text-void-400 hover:text-void-200 transition-colors mb-4">
            <ChevronLeft className="w-4 h-4" />
            Back to Products
          </Link>

          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <GraffitiLogo className="h-12 w-12" />
              <div>
                <h1 className="text-2xl md:text-3xl font-display font-bold text-void-50">
                  {isAlen ? (
                    <>Scrambler <span className="text-purple-400">Voice Demo</span></>
                  ) : (
                    <>{currentPersona.product} <span className="text-purple-400">Preview</span></>
                  )}
                </h1>
                <p className="text-void-400 text-sm flex items-center gap-1.5">
                  <span>{currentLanguage.flag}</span>
                  <span>{currentLanguage.nativeName}</span>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {isAlen ? (
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  liveMode && remaining !== 0 ? 'bg-emerald-900/30 text-emerald-400 border border-emerald-700/30' : 'bg-amber-900/30 text-amber-400 border border-amber-700/30'
                }`}>
                  {liveMode && remaining !== 0 ? 'Live Demo' : 'Preview Mode'}
                </span>
              ) : (
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${READINESS_STYLES[currentPersona.readiness].chip}`}>
                  {READINESS_STYLES[currentPersona.readiness].label} · Simulated Preview
                </span>
              )}
              <button onClick={() => setShowSettings(!showSettings)} className="p-2 rounded-lg bg-void-800/50 text-void-400 hover:text-void-200 hover:bg-void-700/50 transition-colors">
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Persona switcher - preview other SynthetIQ Signals systems through the same demo shell */}
          <div className="mt-5 flex items-center gap-2 overflow-x-auto pb-1">
            <LayoutGrid className="w-4 h-4 text-void-500 shrink-0" />
            {ALL_PERSONAS.map((persona) => (
              <button
                key={persona.id}
                onClick={() => setSelectedPersonaId(persona.id)}
                className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  persona.id === selectedPersonaId
                    ? 'bg-purple-900/40 text-purple-300 border-purple-600/50'
                    : 'bg-void-800/50 text-void-400 border-void-700/50 hover:text-void-200 hover:bg-void-700/50'
                }`}
              >
                {persona.label}
              </button>
            ))}
          </div>
        </div>

        {/* Settings Panel */}
        <AnimatePresence>
          {showSettings && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-6 overflow-hidden">
              <div className="p-6 rounded-2xl bg-void-900/50 border border-void-800/50 space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-void-100 mb-4 flex items-center gap-2">
                    <Info className="w-5 h-5 text-purple-400" />
                    About This Demo
                  </h3>

                  {!isAlen ? (
                    <div className={`p-4 rounded-xl border ${READINESS_STYLES[currentPersona.readiness].box}`}>
                      <div className="flex items-start gap-3">
                        <Sparkles className={`w-5 h-5 shrink-0 mt-0.5 ${READINESS_STYLES[currentPersona.readiness].icon}`} />
                        <div className={`text-sm ${READINESS_STYLES[currentPersona.readiness].text}`}>
                          <p className="font-medium mb-1">{currentPersona.product} — {READINESS_STYLES[currentPersona.readiness].label}</p>
                          <p className={READINESS_STYLES[currentPersona.readiness].subtext}>{currentPersona.readinessNote}</p>
                        </div>
                      </div>
                    </div>
                  ) : liveMode && remaining !== 0 ? (
                    <div className="p-4 rounded-xl bg-emerald-900/20 border border-emerald-700/30">
                      <div className="flex items-start gap-3">
                        <Sparkles className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                        <div className="text-sm text-emerald-200">
                          <p className="font-medium mb-1">Live Demo Active</p>
                          <p className="text-emerald-300/80">
                            Replies come from a small, rate-limited live model running behind our own server
                            — nothing is sent directly from your browser to a third party, and no key or
                            personal data is required from you. Limited to a handful of replies per visit
                            {remaining !== null ? ` (${remaining} left)` : ''}; after that, or anytime the live
                            demo is unavailable, this switches to simulated preview responses automatically.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 rounded-xl bg-amber-900/20 border border-amber-700/30">
                      <div className="flex items-start gap-3">
                        <Sparkles className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                        <div className="text-sm text-amber-200">
                          <p className="font-medium mb-1">Preview Mode</p>
                          <p className="text-amber-300/80">
                            Responses are simulated{remaining === 0 ? ' — you’ve used this visit’s live demo replies' : ''}.
                            Voice input/output still work fully. Try asking about the Scrambler, privacy, or features.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t border-void-800/50">
                  <h3 className="text-lg font-semibold text-void-100 mb-4 flex items-center gap-2">
                    <Globe className="w-5 h-5 text-purple-400" />
                    Voice & Language
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Dropdown
                      value={selectedLanguage}
                      onChange={setSelectedLanguage}
                      options={languageOptions}
                      icon={Globe}
                      label="Language"
                      renderOption={(option) => (
                        <span className="flex items-center gap-2">
                          <span>{option.flag}</span>
                          <span>{option.label}</span>
                        </span>
                      )}
                    />
                    <Dropdown
                      value={selectedVoice}
                      onChange={setSelectedVoice}
                      options={voiceOptions}
                      icon={AudioLines}
                      label="Voice"
                      renderOption={(option) => <span className="truncate">{option.label}</span>}
                    />
                  </div>
                  {filteredVoices.length === 0 && voicesLoaded && (
                    <p className="text-xs text-amber-400 mt-2">
                      No {currentLanguage.name} voice is installed on this device/browser — speech output will use your system default instead of this language's accent.
                    </p>
                  )}
                  <p className="text-xs text-void-500 mt-3">
                    Voice availability depends on your browser and OS. Chrome or Edge generally offer the widest selection.
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Error */}
        <AnimatePresence>
          {error && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="mb-6 p-4 rounded-xl bg-red-900/30 border border-red-700/30 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              <p className="text-red-200 text-sm flex-1">{error}</p>
              <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300 text-xl">×</button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Content */}
        <div className="grid lg:grid-cols-[auto_1fr] gap-8 items-start">
          {/* Device */}
          <div className="hidden lg:block sticky top-28">
            <ScramblerDevice isListening={isListening} isSpeaking={isSpeaking}>
              <div className="aspect-square bg-gradient-to-b from-purple-900/30 to-void-950 p-4">
                <div className="flex justify-between text-[10px] text-purple-300 mb-2">
                  <span>{currentLanguage.flag} {currentLanguage.code}</span>
                  <span className={isListening ? 'text-red-400' : isSpeaking ? 'text-cyan-400' : ''}>
                    {isListening ? 'LISTENING' : isSpeaking ? 'SPEAKING' : 'READY'}
                  </span>
                </div>

                <div className="flex flex-col items-center justify-center h-[calc(100%-20px)]">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-b from-cyan-400 to-cyan-600 flex items-center justify-center mb-3">
                    <div className="flex gap-4">
                      <motion.div animate={{ scaleY: isSpeaking ? [1, 0.5, 1] : 1 }} transition={{ duration: 0.3, repeat: isSpeaking ? Infinity : 0 }} className="w-4 h-4 bg-void-900 rounded-full" />
                      <motion.div animate={{ scaleY: isSpeaking ? [1, 0.5, 1] : 1 }} transition={{ duration: 0.3, repeat: isSpeaking ? Infinity : 0, delay: 0.1 }} className="w-4 h-4 bg-void-900 rounded-full" />
                    </div>
                  </div>
                  <AudioVisualizer isActive={isListening || isSpeaking} />
                </div>
              </div>
            </ScramblerDevice>

            <div className="flex justify-center gap-4 mt-6">
              <button onClick={toggleListening} disabled={isLoading} className={`p-4 rounded-full transition-all ${isListening ? 'bg-red-500 text-white shadow-lg shadow-red-500/50' : 'bg-void-800 text-void-300 hover:bg-void-700'}`}>
                {isListening ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
              </button>
              <button onClick={() => isSpeaking ? stopSpeaking() : setIsMuted(!isMuted)} className={`p-4 rounded-full transition-all ${isMuted ? 'bg-void-700 text-void-400' : 'bg-void-800 text-void-300 hover:bg-void-700'}`}>
                {isSpeaking ? <Square className="w-6 h-6" /> : isMuted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
              </button>
            </div>
            <div className="mt-4 text-center">
              <p className="text-xs text-void-500">Voice: <span className="text-void-400">{selectedVoice || 'System default'}</span></p>
            </div>
          </div>

          {/* Chat */}
          <div className="flex flex-col h-[600px] lg:h-[700px] bg-void-900/30 rounded-3xl border border-void-800/50 overflow-hidden">
            <div className="p-4 border-b border-void-800/50 bg-void-900/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-purple-700 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-void-100">{isAlen ? 'Graffiti™ Assistant' : `${currentPersona.product} Preview`}</h3>
                  <p className="text-xs text-void-500 flex items-center gap-1">
                    {isAlen ? (
                      <>
                        <span className={`w-2 h-2 rounded-full ${liveMode && remaining !== 0 ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                        {liveMode && remaining !== 0 ? 'Live Demo' : 'Preview Mode'}
                      </>
                    ) : (
                      <>
                        <span className={`w-2 h-2 rounded-full ${READINESS_STYLES[currentPersona.readiness].dot}`} />
                        {READINESS_STYLES[currentPersona.readiness].label} · Simulated
                      </>
                    )}
                    <span className="mx-1">•</span>
                    <span>{currentLanguage.flag} {currentLanguage.nativeName}</span>
                  </p>
                </div>
                <button onClick={toggleListening} disabled={isLoading} className={`lg:hidden p-3 rounded-full ${isListening ? 'bg-red-500 text-white' : 'bg-void-800 text-void-300'}`}>
                  {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <MessageBubble key={message.id} message={message} isUser={message.role === 'user'} />
              ))}
              {isLoading && <MessageBubble message={{ content: '' }} isUser={false} isLoading={true} />}
              <div ref={messagesEndRef} />
            </div>

            <form onSubmit={handleSubmit} className="p-4 border-t border-void-800/50 bg-void-900/50">
              <div className="flex gap-3">
                <input type="text" value={inputText} onChange={(e) => setInputText(e.target.value)} placeholder={isListening ? `${currentLanguage.flag} Listening...` : `Type in ${currentLanguage.nativeName}...`} className="flex-1 input-field" disabled={isLoading} />
                <button type="submit" disabled={!inputText.trim() || isLoading} className="btn-primary px-4 disabled:opacity-50">
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-void-500 mt-2 text-center">
                <Shield className="w-3 h-3 inline mr-1" />
                {isAlen
                  ? (liveMode && remaining !== 0 ? 'Voice enabled - ask about privacy, features, or pricing.' : 'Preview mode - voice works! Ask about privacy, features, or pricing.')
                  : `Simulated preview - ask what ${currentPersona.product} does, or how far along it is.`}
              </p>
            </form>
          </div>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Shield, title: 'Private & Secure', desc: 'RSA 2048 encryption' },
            { icon: Mic, title: 'Voice Enabled', desc: 'Natural speech interaction' },
            { icon: Globe, title: 'Multilingual', desc: `${SUPPORTED_LANGUAGES.length} languages` },
            { icon: Sparkles, title: 'AI Powered', desc: 'Advanced language models' },
          ].map((f) => (
            <div key={f.title} className="p-4 rounded-xl bg-void-900/30 border border-void-800/50 text-center">
              <f.icon className="w-6 h-6 text-purple-400 mx-auto mb-2" />
              <h4 className="font-medium text-void-200 text-sm">{f.title}</h4>
              <p className="text-xs text-void-500">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
